import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChartOutlined,
  ReloadOutlined,
  FilterOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  SearchOutlined,
  CalendarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { actionApi } from '../../utils/actionApi';
import { getSuccessMessage } from '../../utils/actionMessageTranslator';
import { getActionApiUrl } from '../../config/api';
import {
  message,
  Spin,
  Input,
  Button,
  Select,
  Card,
  Row,
  Col,
  Space,
  Tabs,
  Table,
  Statistic,
  Tag,
  Empty,
  Tooltip,
} from 'antd';
import { CardListSkeleton } from '../../components/SkeletonLoaders';
import PersianDatePicker from '../../components/PersianDatePicker';
import { toGregorian, toJalali } from '../../utils/dateUtils';
import { employeesApi } from '../../utils/api';
import ProgressBadge from '../../components/action/ProgressBadge';
import { Progress } from 'antd';

const { TabPane } = Tabs;

// استایل دکمه primary مطابق با بقیه دکمه‌های پنل
const primaryButtonClass = '!bg-emerald-500 hover:!bg-emerald-600 !border-emerald-500 hover:!border-emerald-600 !text-white';

const ActionReports = () => {
  const navigate = useNavigate();
  const { actionToken } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  // States for General Report
  const [generalReport, setGeneralReport] = useState([]);
  const [generalPagination, setGeneralPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  
  // States for Progress Report
  const [progressReport, setProgressReport] = useState(null);
  
  // States for Statistics Report
  const [statisticsReport, setStatisticsReport] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    action_type: '',
    source_module: '',
    requester_id: '',
    assigned_to: '',
    raci_role: '',
    raci_user_id: '',
    date_from: '',
    date_to: '',
    search: '',
    overdue: false,
    sort: 'created_at',
    order: 'desc',
    format: 'xlsx',
  });
  
  // Employees for select
  const [erpEmployees, setErpEmployees] = useState([]);
  const [erpLoading, setErpLoading] = useState(false);

  useEffect(() => {
    if (actionToken) {
      loadErpEmployees();
      loadCurrentTab();
    }
  }, [actionToken, activeTab]);

  useEffect(() => {
    if (actionToken && activeTab === 'general') {
      loadGeneralReport(1);
    }
  }, [filters, actionToken, activeTab]);

  const loadErpEmployees = async () => {
    setErpLoading(true);
    try {
      const res = await employeesApi.getAll({});
      const list = res?.data || [];
      setErpEmployees(
        list.map((emp) => ({
          value: emp.id,
          label: `${emp.name || 'بدون نام'} (${emp.email || 'بدون ایمیل'})`,
        }))
      );
    } catch (error) {
      console.error('Load employees error:', error);
    } finally {
      setErpLoading(false);
    }
  };

  const loadCurrentTab = () => {
    if (activeTab === 'general') {
      loadGeneralReport(1);
    } else if (activeTab === 'progress') {
      loadProgressReport();
    } else if (activeTab === 'statistics') {
      loadStatisticsReport();
    }
  };

  const loadGeneralReport = async (page = 1) => {
    if (!actionToken) return;
    setLoading(true);
    try {
      const res = await actionApi.getActionsReport({
        ...filters,
        page,
        per_page: generalPagination.per_page,
      });
      const list = res?.data?.data || [];
      const pag = res?.data?.pagination || generalPagination;
      setGeneralReport(list);
      setGeneralPagination(pag);
    } catch (error) {
      console.error('Load general report error:', error);
      message.error('خطا در دریافت گزارش کلی اقدامات');
    } finally {
      setLoading(false);
    }
  };

  const loadProgressReport = async () => {
    if (!actionToken) return;
    setLoading(true);
    try {
      const res = await actionApi.getActionsProgressReport(filters);
      setProgressReport(res?.data || null);
    } catch (error) {
      console.error('Load progress report error:', error);
      message.error('خطا در دریافت گزارش پیشرفت');
    } finally {
      setLoading(false);
    }
  };

  const loadStatisticsReport = async () => {
    if (!actionToken) return;
    setLoading(true);
    try {
      const res = await actionApi.getActionsStatistics(filters);
      setStatisticsReport(res?.data || null);
    } catch (error) {
      console.error('Load statistics report error:', error);
      message.error('خطا در دریافت آمار اقدامات');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (isProgress = false) => {
    if (!actionToken) return;
    setExporting(true);
    try {
      const exportFilters = { ...filters };
      let res;
      
      if (isProgress) {
        res = await actionApi.exportActionsProgress(exportFilters);
      } else {
        res = await actionApi.exportActions(exportFilters);
      }

        if (res?.success) {
        // بررسی حالت queued
        if (res?.data?.status === 'queued') {
          message.info({
            content: 'درخواست export در صف قرار گرفت. پس از آماده شدن فایل، به شما اطلاع داده می‌شود.',
            duration: 6,
          });
          return;
        }

        // اگر download_url موجود باشد، از آن استفاده می‌کنیم
          if (res?.data?.download_url) {
          const downloadUrl = res.data.download_url;
          // استخراج filePath از download_url
          // مثال: /api/v1/actions/export/download/exports/actions_20240101_120000.xlsx
          const urlMatch = downloadUrl.match(/\/export\/download\/(.+)$/);
          if (urlMatch && urlMatch[1]) {
            const filePath = urlMatch[1];
            try {
              await actionApi.downloadExportFile(filePath);
            message.success('فایل با موفقیت دانلود شد');
            } catch (downloadError) {
              console.error('Download error:', downloadError);
              // در صورت خطا در دانلود، سعی می‌کنیم از URL مستقیم استفاده کنیم
              const fullUrl = downloadUrl.startsWith('http')
                ? downloadUrl
                : getActionApiUrl(downloadUrl);
              window.open(fullUrl, '_blank');
              message.success('فایل در حال دانلود است');
        }
      } else {
            // اگر نتوانستیم filePath را استخراج کنیم، از URL مستقیم استفاده می‌کنیم
            const fullUrl = downloadUrl.startsWith('http')
              ? downloadUrl
              : getActionApiUrl(downloadUrl);
            window.open(fullUrl, '_blank');
            message.success('فایل در حال دانلود است');
          }
        } 
        // اگر file_path موجود باشد اما download_url نباشد
        else if (res?.data?.file_path) {
          try {
            await actionApi.downloadExportFile(res.data.file_path);
            message.success('فایل با موفقیت دانلود شد');
          } catch (downloadError) {
            console.error('Download error:', downloadError);
            message.error('خطا در دانلود فایل');
          }
        } else {
          message.warning('اطلاعات فایل برای دانلود یافت نشد');
        }
      } else {
        message.error(res?.message || 'خطا در خروجی گرفتن از گزارش');
      }
    } catch (error) {
      console.error('Export error:', error);
      message.error(error?.message || 'خطا در خروجی گرفتن از گزارش');
    } finally {
      setExporting(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const gregorianStart = toGregorian(start);
      const gregorianEnd = toGregorian(end);
      setFilters((prev) => ({
        ...prev,
        date_from: gregorianStart,
        date_to: gregorianEnd,
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        date_from: '',
        date_to: '',
      }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      status: '',
      priority: '',
      action_type: '',
      source_module: '',
      requester_id: '',
      assigned_to: '',
      raci_role: '',
      raci_user_id: '',
      date_from: '',
      date_to: '',
      search: '',
      overdue: false,
      sort: 'created_at',
      order: 'desc',
      format: 'xlsx',
    });
  };

  const statusOptions = [
    { value: 'باز', label: 'باز' },
    { value: 'در_حال_برنامه_ریزی', label: 'در حال برنامه‌ریزی' },
    { value: 'در_حال_انجام', label: 'در حال انجام' },
    { value: 'بسته', label: 'بسته' },
  ];

  const priorityOptions = [
    { value: 'فوری_و_اضطراری', label: 'فوری و اضطراری' },
    { value: 'بسیار_مهم', label: 'بسیار مهم' },
    { value: 'مهم', label: 'مهم' },
    { value: 'کم_اهمیت', label: 'کم اهمیت' },
    { value: 'غیرضروری', label: 'غیرضروری' },
  ];

  const actionTypeOptions = [
    { value: 'اصلاح', label: 'اصلاح' },
    { value: 'اقدام_پیشگیرانه', label: 'اقدام پیشگیرانه' },
    { value: 'اقدام_اصلاحی', label: 'اقدام اصلاحی' },
  ];

  const raciRoleOptions = [
    { value: 'پاسخگو', label: 'پاسخگو' },
    { value: 'مسئول_اجرا', label: 'مسئول اجرا' },
    { value: 'مطلع', label: 'مطلع' },
    { value: 'مشاور', label: 'مشاور' },
  ];

  const sortOptions = [
    { value: 'priority', label: 'اولویت' },
    { value: 'action_type', label: 'نوع اقدام' },
    { value: 'source_module', label: 'ماژول منبع' },
    { value: 'created_at', label: 'تاریخ ایجاد' },
    { value: 'updated_at', label: 'تاریخ به‌روزرسانی' },
  ];

  const formatOptions = [
    { value: 'xlsx', label: 'Excel (xlsx)' },
    { value: 'xls', label: 'Excel (xls)' },
    { value: 'csv', label: 'CSV' },
  ];

  const generalColumns = [
    {
      title: 'شماره',
      dataIndex: 'number',
      key: 'number',
      width: 140,
      fixed: 'left',
      render: (text) => (
        <span style={{ fontFamily: 'CustomFont', fontWeight: 600 }}>{text}</span>
      ),
    },
    {
      title: 'عنوان',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => (
        <Tooltip title={text}>
          <div>
            <div style={{ fontFamily: 'CustomFont', fontWeight: 500, marginBottom: 4 }}>
              {text}
            </div>
            {record.description && (
              <div
                style={{
                  fontFamily: 'CustomFont',
                  fontSize: 12,
                  color: '#8c8c8c',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {record.description}
              </div>
            )}
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => {
        const statusMap = {
          باز: { color: 'blue', label: 'باز' },
          در_حال_برنامه_ریزی: { color: 'orange', label: 'در حال برنامه‌ریزی' },
          در_حال_انجام: { color: 'processing', label: 'در حال انجام' },
          بسته: { color: 'default', label: 'بسته' },
        };
        const statusInfo = statusMap[status] || { color: 'default', label: status };
        return <Tag color={statusInfo.color} style={{ fontFamily: 'CustomFont' }}>{statusInfo.label}</Tag>;
      },
    },
    {
      title: 'اولویت',
      dataIndex: 'priority',
      key: 'priority',
      width: 130,
      render: (priority) => {
        const priorityMap = {
          فوری_و_اضطراری: { color: 'red', label: 'فوری و اضطراری' },
          بسیار_مهم: { color: 'orange', label: 'بسیار مهم' },
          مهم: { color: 'blue', label: 'مهم' },
          کم_اهمیت: { color: 'cyan', label: 'کم اهمیت' },
          غیرضروری: { color: 'default', label: 'غیرضروری' },
        };
        const priorityInfo = priorityMap[priority] || { color: 'default', label: priority };
        return <Tag color={priorityInfo.color} style={{ fontFamily: 'CustomFont' }}>{priorityInfo.label}</Tag>;
      },
    },
    {
      title: 'نوع اقدام',
      dataIndex: 'action_type',
      key: 'action_type',
      width: 130,
      render: (actionType) => {
        const typeMap = {
          اصلاح: { color: 'purple', label: 'اصلاح' },
          اقدام_پیشگیرانه: { color: 'green', label: 'اقدام پیشگیرانه' },
          اقدام_اصلاحی: { color: 'blue', label: 'اقدام اصلاحی' },
        };
        const typeInfo = typeMap[actionType] || { color: 'default', label: actionType };
        return <Tag color={typeInfo.color} style={{ fontFamily: 'CustomFont' }}>{typeInfo.label}</Tag>;
      },
    },
    {
      title: 'پیشرفت',
      dataIndex: 'percent_complete',
      key: 'percent_complete',
      width: 150,
      render: (percent) => (
        <ProgressBadge
          percent={percent}
          size="sm"
          variant="bar"
          className="w-full"
        />
      ),
    },
    {
      title: 'ماژول منبع',
      dataIndex: 'source_module',
      key: 'source_module',
      width: 130,
      render: (module) => (
        <Tag color="geekblue" style={{ fontFamily: 'CustomFont' }}>{module || '-'}</Tag>
      ),
    },
    {
      title: 'درخواست‌کننده',
      dataIndex: 'requester',
      key: 'requester',
      width: 180,
      render: (requester) => {
        if (!requester) return '-';
        return (
          <div>
            <div style={{ fontFamily: 'CustomFont', fontWeight: 500 }}>
              {requester.name || '-'}
            </div>
            {requester.email && (
              <div
                style={{
                  fontFamily: 'CustomFont',
                  fontSize: 11,
                  color: '#8c8c8c',
                  marginTop: 2,
                }}
              >
                {requester.email}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'تاریخ شروع برنامه‌ریزی',
      dataIndex: 'planned_start_date',
      key: 'planned_start_date',
      width: 160,
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont' }}>{date || '-'}</span>
      ),
    },
    {
      title: 'تاریخ شروع واقعی',
      dataIndex: 'actual_start_date',
      key: 'actual_start_date',
      width: 160,
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont', color: date ? '#52c41a' : '#8c8c8c' }}>
          {date || '-'}
        </span>
      ),
    },
    {
      title: 'تاریخ پایان برنامه‌ریزی',
      dataIndex: 'planned_end_date',
      key: 'planned_end_date',
      width: 160,
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont' }}>{date || '-'}</span>
      ),
    },
    {
      title: 'تاریخ پایان واقعی',
      dataIndex: 'actual_end_date',
      key: 'actual_end_date',
      width: 160,
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont', color: date ? '#52c41a' : '#8c8c8c' }}>
          {date || '-'}
        </span>
      ),
    },
    {
      title: 'معوق',
      dataIndex: 'is_overdue',
      key: 'is_overdue',
      width: 80,
      render: (isOverdue) => (
        <Tag color={isOverdue ? 'red' : 'green'} style={{ fontFamily: 'CustomFont' }}>
          {isOverdue ? 'بله' : 'خیر'}
        </Tag>
      ),
    },
    {
      title: 'تاریخ ایجاد',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date) => (
        <span style={{ fontFamily: 'CustomFont' }}>{date || '-'}</span>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="min-h-screen p-4 md:p-6 lg:p-8" style={{ fontFamily: 'CustomFont' }}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate('/action/dashboard')}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
              <BarChartOutlined className="text-3xl text-blue-500" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold m-0" style={{ fontFamily: 'CustomFont' }}>
                  گزارشات اقدامات
                </h1>
                <p className="text-gray-500 m-0 mt-1" style={{ fontFamily: 'CustomFont' }}>
                  مشاهده و تحلیل گزارش‌های مختلف اقدامات
                </p>
              </div>
            </div>
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFiltersOpen(!filtersOpen)}
                style={{ fontFamily: 'CustomFont' }}
              >
                {filtersOpen ? 'بستن فیلترها' : 'فیلترها'}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadCurrentTab}
                loading={loading}
                style={{ fontFamily: 'CustomFont' }}
              >
                به‌روزرسانی
              </Button>
            </Space>
          </div>

          {/* Filters Panel */}
          {filtersOpen && (
            <Card className="mb-6" style={{ fontFamily: 'CustomFont' }}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    جستجو
                  </label>
                  <Input
                    placeholder="جستجو در عنوان و توضیحات"
                    prefix={<SearchOutlined />}
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    وضعیت
                  </label>
                  <Select
                    placeholder="انتخاب وضعیت"
                    value={filters.status || undefined}
                    onChange={(val) => handleFilterChange('status', val)}
                    allowClear
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={statusOptions}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    اولویت
                  </label>
                  <Select
                    placeholder="انتخاب اولویت"
                    value={filters.priority || undefined}
                    onChange={(val) => handleFilterChange('priority', val)}
                    allowClear
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={priorityOptions}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    نوع اقدام
                  </label>
                  <Select
                    placeholder="انتخاب نوع اقدام"
                    value={filters.action_type || undefined}
                    onChange={(val) => handleFilterChange('action_type', val)}
                    allowClear
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={actionTypeOptions}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    ماژول منبع
                  </label>
                  <Input
                    placeholder="ماژول منبع"
                    value={filters.source_module}
                    onChange={(e) => handleFilterChange('source_module', e.target.value)}
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    درخواست‌کننده
                  </label>
                  <Select
                    placeholder="انتخاب درخواست‌کننده"
                    value={filters.requester_id || undefined}
                    onChange={(val) => handleFilterChange('requester_id', val)}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    loading={erpLoading}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={erpEmployees}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    اختصاص داده شده به
                  </label>
                  <Select
                    placeholder="انتخاب کاربر"
                    value={filters.assigned_to || undefined}
                    onChange={(val) => handleFilterChange('assigned_to', val)}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    loading={erpLoading}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={erpEmployees}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    نقش RACI
                  </label>
                  <Select
                    placeholder="انتخاب نقش RACI"
                    value={filters.raci_role || undefined}
                    onChange={(val) => handleFilterChange('raci_role', val)}
                    allowClear
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={raciRoleOptions}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    کاربر RACI
                  </label>
                  <Select
                    placeholder="انتخاب کاربر RACI"
                    value={filters.raci_user_id || undefined}
                    onChange={(val) => handleFilterChange('raci_user_id', val)}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    loading={erpLoading}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={erpEmployees}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    بازه زمانی
                  </label>
                  <PersianDatePicker
                    range
                    placeholder={['از تاریخ', 'تا تاریخ']}
                    value={
                      filters.date_from && filters.date_to
                        ? [toJalali(filters.date_from), toJalali(filters.date_to)]
                        : null
                    }
                    onChange={handleDateRangeChange}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    اقدامات معوق
                  </label>
                  <Select
                    placeholder="انتخاب"
                    value={filters.overdue ? 'true' : 'false'}
                    onChange={(val) => handleFilterChange('overdue', val === 'true')}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={[
                      { value: 'false', label: 'همه' },
                      { value: 'true', label: 'فقط معوق' },
                    ]}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    مرتب‌سازی بر اساس
                  </label>
                  <Select
                    placeholder="انتخاب فیلد"
                    value={filters.sort || undefined}
                    onChange={(val) => handleFilterChange('sort', val)}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={sortOptions}
                  />
                </div>
                <div>
                  <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                    جهت مرتب‌سازی
                  </label>
                  <Select
                    placeholder="انتخاب جهت"
                    value={filters.order || undefined}
                    onChange={(val) => handleFilterChange('order', val)}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    options={[
                      { value: 'asc', label: 'صعودی' },
                      { value: 'desc', label: 'نزولی' },
                    ]}
                  />
                </div>
                {activeTab === 'general' && (
                  <div>
                    <label className="block mb-2" style={{ fontFamily: 'CustomFont' }}>
                      فرمت خروجی
                    </label>
                    <Select
                      placeholder="انتخاب فرمت"
                      value={filters.format || 'xlsx'}
                      onChange={(val) => handleFilterChange('format', val)}
                      style={{ width: '100%', fontFamily: 'CustomFont' }}
                      options={formatOptions}
                    />
                  </div>
                )}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button onClick={handleResetFilters} style={{ fontFamily: 'CustomFont' }}>
                  پاک کردن فیلترها
                </Button>
              </div>
            </Card>
          )}

          {/* Tabs */}
          <Card style={{ fontFamily: 'CustomFont' }}>
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              style={{ fontFamily: 'CustomFont' }}
              tabBarExtraContent={
                <Space>
                  {activeTab === 'general' && (
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleExport(false)}
                      loading={exporting}
                      type="primary"
                      className={primaryButtonClass}
                      style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
                    >
                      خروجی Excel
                    </Button>
                  )}
                  {activeTab === 'progress' && (
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleExport(true)}
                      loading={exporting}
                      type="primary"
                      className={primaryButtonClass}
                      style={{ borderRadius: 14, fontFamily: 'CustomFont' }}
                    >
                      خروجی Excel
                    </Button>
                  )}
                </Space>
              }
            >
              <TabPane
                tab={
                  <span style={{ fontFamily: 'CustomFont' }}>
                    <BarChartOutlined /> گزارش کلی
                  </span>
                }
                key="general"
              >
                <Spin spinning={loading}>
                  {generalReport.length === 0 && !loading ? (
                    <Empty
                      description="گزارشی یافت نشد"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  ) : (
                    <>
                      <Table
                        columns={generalColumns}
                        dataSource={generalReport}
                        rowKey="id"
                        scroll={{ x: 1800 }}
                        pagination={{
                          current: generalPagination.current_page,
                          pageSize: generalPagination.per_page,
                          total: generalPagination.total,
                          showSizeChanger: true,
                          showTotal: (total) => `مجموع ${total} مورد`,
                          onChange: (page, pageSize) => {
                            setGeneralPagination((prev) => ({
                              ...prev,
                              per_page: pageSize,
                            }));
                            loadGeneralReport(page);
                          },
                        }}
                        style={{ fontFamily: 'CustomFont' }}
                      />
                    </>
                  )}
                </Spin>
              </TabPane>

              <TabPane
                tab={
                  <span style={{ fontFamily: 'CustomFont' }}>
                    <ClockCircleOutlined /> گزارش پیشرفت
                  </span>
                }
                key="progress"
              >
                <Spin spinning={loading}>
                  {progressReport ? (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="کل اقدامات"
                            value={progressReport.total_actions || 0}
                            prefix={<BarChartOutlined />}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="تکمیل شده"
                            value={progressReport.completed || 0}
                            prefix={<CheckCircleOutlined />}
                            valueStyle={{ color: '#3f8600' }}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="در حال انجام"
                            value={progressReport.in_progress || 0}
                            prefix={<ClockCircleOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="در انتظار"
                            value={progressReport.pending || 0}
                            prefix={<WarningOutlined />}
                            valueStyle={{ color: '#faad14' }}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={24} md={12}>
                        <Card>
                          <Statistic
                            title="میانگین پیشرفت"
                            value={progressReport.average_progress || 0}
                            suffix="%"
                            prefix={<BarChartOutlined />}
                            valueStyle={{ color: '#722ed1' }}
                            style={{ fontFamily: 'CustomFont' }}
                          />
                        </Card>
                      </Col>
                    </Row>
                  ) : (
                    <Empty
                      description="گزارشی یافت نشد"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  )}
                </Spin>
              </TabPane>

              <TabPane
                tab={
                  <span style={{ fontFamily: 'CustomFont' }}>
                    <BarChartOutlined /> آمار کلی
                  </span>
                }
                key="statistics"
              >
                <Spin spinning={loading}>
                  {statisticsReport ? (
                    <div className="space-y-6">
                      {/* آمار کلی - کارت‌های اصلی */}
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                          <Card
                            style={{
                              borderRadius: 16,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              border: 'none',
                            }}
                            bodyStyle={{ padding: 20 }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div
                                  style={{
                                    fontFamily: 'CustomFont',
                                    fontSize: 14,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    marginBottom: 8,
                                  }}
                                >
                                  کل اقدامات
                                </div>
                                <div
                                  style={{
                                    fontFamily: 'CustomFont',
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: '#fff',
                                  }}
                                >
                                  {statisticsReport.total?.toLocaleString('fa-IR') || 0}
                                </div>
                              </div>
                              <div
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 12,
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <BarChartOutlined style={{ fontSize: 28, color: '#fff' }} />
                              </div>
                            </div>
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Card
                            style={{
                              borderRadius: 16,
                              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                              border: 'none',
                            }}
                            bodyStyle={{ padding: 20 }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div
                                  style={{
                                    fontFamily: 'CustomFont',
                                    fontSize: 14,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    marginBottom: 8,
                                  }}
                                >
                                  اقدامات معوق
                                </div>
                                <div
                                  style={{
                                    fontFamily: 'CustomFont',
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: '#fff',
                                  }}
                                >
                                  {statisticsReport.overdue?.toLocaleString('fa-IR') || 0}
                                </div>
                              </div>
                              <div
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 12,
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <WarningOutlined style={{ fontSize: 28, color: '#fff' }} />
                              </div>
                            </div>
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Card
                            style={{
                              borderRadius: 16,
                              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                              border: 'none',
                            }}
                            bodyStyle={{ padding: 20 }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div
                                  style={{
                                    fontFamily: 'CustomFont',
                                    fontSize: 14,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    marginBottom: 8,
                                  }}
                                >
                                  با تفکیک
                                </div>
                                <div
                                  style={{
                                    fontFamily: 'CustomFont',
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: '#fff',
                                  }}
                                >
                                  {statisticsReport.with_breakdown?.toLocaleString('fa-IR') || 0}
                                </div>
                              </div>
                              <div
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 12,
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <CheckCircleOutlined style={{ fontSize: 28, color: '#fff' }} />
                              </div>
                            </div>
                          </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                          <Card
                            style={{
                              borderRadius: 16,
                              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                              border: 'none',
                            }}
                            bodyStyle={{ padding: 20 }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div
                                  style={{
                                    fontFamily: 'CustomFont',
                                    fontSize: 14,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    marginBottom: 8,
                                  }}
                                >
                                  میانگین پیشرفت
                                </div>
                                <div
                                  style={{
                                    fontFamily: 'CustomFont',
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: '#fff',
                                  }}
                                >
                                  {statisticsReport.average_progress
                                    ? Number(statisticsReport.average_progress).toFixed(1)
                                    : 0}
                                  <span style={{ fontSize: 18, marginRight: 4 }}>%</span>
                                </div>
                              </div>
                              <div
                                style={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 12,
                                  background: 'rgba(255, 255, 255, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <ClockCircleOutlined style={{ fontSize: 28, color: '#fff' }} />
                              </div>
                            </div>
                          </Card>
                        </Col>
                      </Row>

                      {/* توزیع بر اساس وضعیت */}
                      {statisticsReport.by_status && (
                        <Card
                          title={
                            <span style={{ fontFamily: 'CustomFont', fontSize: 16, fontWeight: 600 }}>
                              توزیع بر اساس وضعیت
                            </span>
                          }
                          style={{ borderRadius: 16, fontFamily: 'CustomFont' }}
                          bodyStyle={{ padding: 24 }}
                        >
                          <Row gutter={[16, 16]}>
                            {Object.entries(statisticsReport.by_status).map(([status, count]) => {
                              const total = statisticsReport.total || 1;
                              const percentage = ((count / total) * 100).toFixed(1);
                              const statusColors = {
                                باز: { color: '#1890ff', bg: '#e6f7ff' },
                                در_حال_برنامه_ریزی: { color: '#fa8c16', bg: '#fff7e6' },
                                در_حال_انجام: { color: '#722ed1', bg: '#f9f0ff' },
                                بسته: { color: '#52c41a', bg: '#f6ffed' },
                              };
                              const statusInfo = statusColors[status] || {
                                color: '#8c8c8c',
                                bg: '#f5f5f5',
                              };
                              const statusLabel =
                                status === 'در_حال_برنامه_ریزی'
                                  ? 'در حال برنامه‌ریزی'
                                  : status === 'در_حال_انجام'
                                  ? 'در حال انجام'
                                  : status;

                              return (
                              <Col xs={24} sm={12} md={6} key={status}>
                                  <div
                                    style={{
                                      padding: 20,
                                      borderRadius: 12,
                                      background: statusInfo.bg,
                                      border: `1px solid ${statusInfo.color}20`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 13,
                                        color: '#8c8c8c',
                                        marginBottom: 8,
                                      }}
                                    >
                                      {statusLabel}
                                    </div>
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 28,
                                        fontWeight: 700,
                                        color: statusInfo.color,
                                        marginBottom: 12,
                                      }}
                                    >
                                      {count.toLocaleString('fa-IR')}
                                    </div>
                                    <Progress
                                      percent={Number(percentage)}
                                      strokeColor={statusInfo.color}
                                      showInfo={false}
                                      size="small"
                                    />
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 12,
                                        color: '#8c8c8c',
                                        marginTop: 8,
                                      }}
                                    >
                                      {percentage}% از کل
                                    </div>
                                  </div>
                              </Col>
                              );
                            })}
                          </Row>
                        </Card>
                      )}

                      {/* توزیع بر اساس اولویت */}
                      {statisticsReport.by_priority && (
                        <Card
                          title={
                            <span style={{ fontFamily: 'CustomFont', fontSize: 16, fontWeight: 600 }}>
                              توزیع بر اساس اولویت
                            </span>
                          }
                          style={{ borderRadius: 16, fontFamily: 'CustomFont' }}
                          bodyStyle={{ padding: 24 }}
                        >
                          <Row gutter={[16, 16]}>
                            {Object.entries(statisticsReport.by_priority).map(([priority, count]) => {
                              const total = statisticsReport.total || 1;
                              const percentage = ((count / total) * 100).toFixed(1);
                              const priorityColors = {
                                فوری_و_اضطراری: { color: '#ff4d4f', bg: '#fff1f0' },
                                بسیار_مهم: { color: '#ff7a45', bg: '#fff2e8' },
                                مهم: { color: '#1890ff', bg: '#e6f7ff' },
                                کم_اهمیت: { color: '#13c2c2', bg: '#e6fffb' },
                                غیرضروری: { color: '#8c8c8c', bg: '#f5f5f5' },
                              };
                              const priorityInfo = priorityColors[priority] || {
                                color: '#8c8c8c',
                                bg: '#f5f5f5',
                              };
                              const priorityLabel =
                                priority === 'فوری_و_اضطراری'
                                  ? 'فوری و اضطراری'
                                  : priority === 'بسیار_مهم'
                                  ? 'بسیار مهم'
                                  : priority === 'کم_اهمیت'
                                  ? 'کم اهمیت'
                                  : priority;

                              return (
                              <Col xs={24} sm={12} md={6} key={priority}>
                                  <div
                                    style={{
                                      padding: 20,
                                      borderRadius: 12,
                                      background: priorityInfo.bg,
                                      border: `1px solid ${priorityInfo.color}20`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 13,
                                        color: '#8c8c8c',
                                        marginBottom: 8,
                                      }}
                                    >
                                      {priorityLabel}
                                    </div>
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 28,
                                        fontWeight: 700,
                                        color: priorityInfo.color,
                                        marginBottom: 12,
                                      }}
                                    >
                                      {count.toLocaleString('fa-IR')}
                                    </div>
                                    <Progress
                                      percent={Number(percentage)}
                                      strokeColor={priorityInfo.color}
                                      showInfo={false}
                                      size="small"
                                    />
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 12,
                                        color: '#8c8c8c',
                                        marginTop: 8,
                                      }}
                                    >
                                      {percentage}% از کل
                                    </div>
                                  </div>
                              </Col>
                              );
                            })}
                          </Row>
                        </Card>
                      )}

                      {/* توزیع بر اساس نوع اقدام */}
                      {statisticsReport.by_action_type && (
                        <Card
                          title={
                            <span style={{ fontFamily: 'CustomFont', fontSize: 16, fontWeight: 600 }}>
                              توزیع بر اساس نوع اقدام
                            </span>
                          }
                          style={{ borderRadius: 16, fontFamily: 'CustomFont' }}
                          bodyStyle={{ padding: 24 }}
                        >
                          <Row gutter={[16, 16]}>
                            {Object.entries(statisticsReport.by_action_type).map(([actionType, count]) => {
                              const total = statisticsReport.total || 1;
                              const percentage = ((count / total) * 100).toFixed(1);
                              const typeColors = {
                                اصلاح: { color: '#722ed1', bg: '#f9f0ff' },
                                اقدام_پیشگیرانه: { color: '#52c41a', bg: '#f6ffed' },
                                اقدام_اصلاحی: { color: '#1890ff', bg: '#e6f7ff' },
                              };
                              const typeInfo = typeColors[actionType] || {
                                color: '#8c8c8c',
                                bg: '#f5f5f5',
                              };
                              const typeLabel =
                                actionType === 'اقدام_پیشگیرانه'
                                  ? 'اقدام پیشگیرانه'
                                  : actionType === 'اقدام_اصلاحی'
                                  ? 'اقدام اصلاحی'
                                  : actionType;

                              return (
                                <Col xs={24} sm={12} md={8} key={actionType}>
                                  <div
                                    style={{
                                      padding: 20,
                                      borderRadius: 12,
                                      background: typeInfo.bg,
                                      border: `1px solid ${typeInfo.color}20`,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 13,
                                        color: '#8c8c8c',
                                        marginBottom: 8,
                                      }}
                                    >
                                      {typeLabel}
                                    </div>
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 28,
                                        fontWeight: 700,
                                        color: typeInfo.color,
                                        marginBottom: 12,
                                      }}
                                    >
                                      {count.toLocaleString('fa-IR')}
                                    </div>
                                    <Progress
                                      percent={Number(percentage)}
                                      strokeColor={typeInfo.color}
                                      showInfo={false}
                                      size="small"
                                    />
                                    <div
                                      style={{
                                        fontFamily: 'CustomFont',
                                        fontSize: 12,
                                        color: '#8c8c8c',
                                        marginTop: 8,
                                      }}
                                    >
                                      {percentage}% از کل
                                    </div>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>
                        </Card>
                      )}

                      {/* توزیع بر اساس ماژول منبع */}
                      {statisticsReport.by_source_module && (
                        <Card
                          title={
                            <span style={{ fontFamily: 'CustomFont', fontSize: 16, fontWeight: 600 }}>
                              توزیع بر اساس ماژول منبع
                            </span>
                          }
                          style={{ borderRadius: 16, fontFamily: 'CustomFont' }}
                          bodyStyle={{ padding: 24 }}
                        >
                          <Row gutter={[16, 16]}>
                            {Object.entries(statisticsReport.by_source_module)
                              .sort(([, a], [, b]) => b - a)
                              .map(([module, count]) => {
                                const total = statisticsReport.total || 1;
                                const percentage = ((count / total) * 100).toFixed(1);

                                return (
                                  <Col xs={24} sm={12} md={8} lg={6} key={module}>
                                    <div
                                      style={{
                                        padding: 16,
                                        borderRadius: 12,
                                        background: '#fafafa',
                                        border: '1px solid #e8e8e8',
                                        transition: 'all 0.3s',
                                      }}
                                      className="hover:shadow-md"
                                    >
                                      <div
                                        style={{
                                          fontFamily: 'CustomFont',
                                          fontSize: 12,
                                          color: '#8c8c8c',
                                          marginBottom: 8,
                                          textTransform: 'uppercase',
                                        }}
                                      >
                                        {module}
                                      </div>
                                      <div
                                        style={{
                                          fontFamily: 'CustomFont',
                                          fontSize: 24,
                                          fontWeight: 700,
                                          color: '#1890ff',
                                          marginBottom: 8,
                                        }}
                                      >
                                        {count.toLocaleString('fa-IR')}
                                      </div>
                                      <Progress
                                        percent={Number(percentage)}
                                        strokeColor="#1890ff"
                                        showInfo={false}
                                        size="small"
                                      />
                                      <div
                                        style={{
                                          fontFamily: 'CustomFont',
                                          fontSize: 11,
                                          color: '#8c8c8c',
                                          marginTop: 6,
                                        }}
                                      >
                                        {percentage}% از کل
                                      </div>
                                    </div>
                                  </Col>
                                );
                              })}
                          </Row>
                        </Card>
                      )}
                    </div>
                  ) : (
                    <Empty
                      description="گزارشی یافت نشد"
                      style={{ fontFamily: 'CustomFont' }}
                    />
                  )}
                </Spin>
              </TabPane>
            </Tabs>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default ActionReports;

